import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import { usePokCore } from '../hooks/usePokCore';

import { GetTooltipDto } from './autogenerated/pokApiClient';

interface TooltipDisplayProviderType {
  getTextByKey: (key: string) => string;
  refreshTooltips: () => void;
}

const TooltipDisplayContext = createContext<TooltipDisplayProviderType>({
  getTextByKey: () => '',
  refreshTooltips: () => {},
});

export const useTooltipDisplay = () => useContext(TooltipDisplayContext);

export const TooltipDisplayProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const pok = usePokCore();
  const [tooltipMap, setTooltipMap] = useState<Record<string, GetTooltipDto>>(
    {},
  );

  const fetchTooltips = () => {
    pok.tooltips
      .getAll()
      .then(data => {
        setTooltipMap(Object.fromEntries(data.map(t => [t.key, t])));
      })
      .catch(() => {
        // tooltips are non-critical — silent fail
      });
  };

  useEffect(() => {
    fetchTooltips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pok]);

  const getTextByKey = (key: string): string => {
    const tooltip = tooltipMap[key];
    if (!tooltip) {
      return '';
    }
    const lang = pok.getLanguage().toUpperCase();
    return lang === 'EN' ? tooltip.enText : tooltip.plText;
  };

  return (
    <TooltipDisplayContext.Provider
      value={{ getTextByKey, refreshTooltips: fetchTooltips }}
    >
      {children}
    </TooltipDisplayContext.Provider>
  );
};
